import { useState, useEffect, useCallback } from 'react';
import { getAllCouples, toggleSharing } from '../services/coupleService';
import { saveCounselorNotes, getCounselorNotes } from '../services/counselorService';
import { getAssessment } from '../services/assessmentService';
import { useAuth } from '../contexts/AuthContext';

export const useCounselor = () => {
  const { currentUser, userRole } = useAuth();
  const [couples, setCouples] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCouples = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    
    // Pass counselorId to get only this counselor's couples
    const couplesData = await getAllCouples(currentUser.uid);
    
    // Get assessment status for each couple
    const couplesWithStatus = await Promise.all(
      couplesData.map(async (couple) => {
        const partner1Assessment = await getAssessment(couple.partner1Id);
        const partner2Assessment = await getAssessment(couple.partner2Id);
        
        const partner1Status = partner1Assessment?.status || 'not-started';
        const partner2Status = partner2Assessment?.status || 'not-started';
        
        let overallStatus = 'not-started';
        if (partner1Status === 'completed' && partner2Status === 'completed') {
          overallStatus = 'completed';
        } else if (partner1Status !== 'not-started' || partner2Status !== 'not-started') {
          overallStatus = 'in-progress';
        }
        
        return {
          ...couple,
          partner1Assessment,
          partner2Assessment,
          overallStatus
        };
      })
    );
    
    setCouples(couplesWithStatus);
    setLoading(false);
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && userRole === 'counselor') {
      loadCouples();
    }
  }, [currentUser, userRole, loadCouples]);

  const updateSharing = async (coupleId, enabled) => {
    const result = await toggleSharing(coupleId, enabled);
    if (result.success) {
      setCouples(couples.map(c => 
        c.id === coupleId ? { ...c, sharingEnabled: enabled } : c
      ));
    }
    return result;
  };

  const saveNotes = async (coupleId, notes) => {
    if (!currentUser) return { success: false };
    return await saveCounselorNotes(coupleId, currentUser.uid, notes);
  };

  const getNotes = async (coupleId) => {
    if (!currentUser) return { notes: '' };
    return await getCounselorNotes(coupleId, currentUser.uid);
  };

  return {
    couples,
    loading,
    updateSharing,
    saveNotes,
    getNotes,
    reload: loadCouples
  };
};